import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { worldCupForecastApi } from '@wc26-forecast/api/client';
import { mapUserPredictionToWorldCupState } from '@wc26-forecast/api/transformers';
import type { GetUserWorldCupPredictionResponse } from '@wc26-forecast/api/types';
import { GROUP_PLACES, GROUP_SELECTABLE_PLACES, initialState } from './constants';
import {
  clearInvalidBestThirdTeams,
  clearMatchResultsByTeamIds,
  clearNextMatchResults,
  resolveWorldCupMatchSourceTeamId,
} from './lib';
import type { SetFinalMatchScoreSidePayload, WorldCupGroupId, WorldCupMatchId, WorldCupTeamId } from './types';

export const worldCupForecastSlice = createSlice({
  name: 'worldCupForecast',
  initialState,
  reducers: {
    setUserPrediction: (
      state,
      action: PayloadAction<GetUserWorldCupPredictionResponse>,
    ) => {
      state.prediction = mapUserPredictionToWorldCupState(action.payload, state);
    },
    selectGroupTeam: (
      state,
      action: PayloadAction<{
        groupId: WorldCupGroupId;
        teamId: WorldCupTeamId;
      }>,
    ) => {
      if (state.prediction.hasUserPrediction) {
        return;
      }
      const { groupId, teamId } = action.payload;
      const group = state.groups.byId[groupId];
      if (!group.teamIds.includes(teamId)) {
        return;
      }
      const placements = state.prediction.groupPlacements[groupId] ?? {};
      const previousPlacements = { ...placements };
      const currentPlace = GROUP_PLACES
        .find((place) => placements[place] === teamId);
      if (currentPlace) {
        delete placements[currentPlace];
      } else {
        const freePlace = GROUP_SELECTABLE_PLACES
          .find((place) => !placements[place]);
        if (!freePlace) {
          return;
        }
        placements[freePlace] = teamId;
      }
      delete placements[4];
      const selectedTeamIds = GROUP_SELECTABLE_PLACES
        .map((place) => placements[place])
        .filter((id): id is WorldCupTeamId => Boolean(id));
      if (selectedTeamIds.length === 3) {
        const fourthTeamId = group.teamIds
          .find((id) => !selectedTeamIds.includes(id));
        if (fourthTeamId) {
          placements[4] = fourthTeamId;
        }
      }
      const affectedTeamIds = GROUP_PLACES
        .flatMap((place) => {
          const previousTeamId = previousPlacements[place];
          const currentTeamId = placements[place];
          if (previousTeamId === currentTeamId) {
            return [];
          }
          return [ previousTeamId, currentTeamId ];
        })
        .filter((id): id is WorldCupTeamId => Boolean(id));
      if (affectedTeamIds.length > 0) {
        clearMatchResultsByTeamIds(state, affectedTeamIds);
      }
      state.prediction.groupPlacements[groupId] = placements;
      clearInvalidBestThirdTeams(state);
    },
    selectBestThirdTeam: (
      state,
      action: PayloadAction<{
        groupId: WorldCupGroupId;
        teamId: WorldCupTeamId;
      }>,
    ) => {
      if (state.prediction.hasUserPrediction) {
        return;
      }
      const { groupId, teamId } = action.payload;
      if (state.prediction.groupPlacements[groupId]?.[3] !== teamId) {
        return;
      }
      const selectedSlot = Object
        .entries(state.prediction.bestThirdTeams)
        .find(([ , selectedTeamId ]) => selectedTeamId === teamId);
      if (selectedSlot) {
        const [ bestThirdSlot ] = selectedSlot;
        state.prediction.bestThirdTeams[bestThirdSlot as keyof typeof state.prediction.bestThirdTeams] = null;
        clearMatchResultsByTeamIds(state, [ teamId ]);
        return;
      }
      const availableSlot = Object
        .entries(state.bestThirdRules)
        .find(([ bestThirdSlot, allowedGroups ]) => (
          state.prediction.bestThirdTeams[bestThirdSlot as keyof typeof state.prediction.bestThirdTeams] === null &&
          allowedGroups.includes(groupId)
        ));
      if (!availableSlot) {
        return;
      }
      const [ bestThirdSlot ] = availableSlot;
      state.prediction.bestThirdTeams[bestThirdSlot as keyof typeof state.prediction.bestThirdTeams] = teamId;
    },
    selectMatchWinner: (
      state,
      action: PayloadAction<{
        matchId: WorldCupMatchId;
        winnerTeamId: WorldCupTeamId;
      }>,
    ) => {
      if (state.prediction.hasUserPrediction) {
        return;
      }
      const { matchId, winnerTeamId } = action.payload;
      const match = state.matches.byId[matchId];
      if (!match) {
        return;
      }
      const homeTeamId = resolveWorldCupMatchSourceTeamId(match.homeSource, state);
      const awayTeamId = resolveWorldCupMatchSourceTeamId(match.awaySource, state);
      if (!homeTeamId || !awayTeamId) {
        return;
      }
      const isWinnerInMatch = winnerTeamId === homeTeamId || winnerTeamId === awayTeamId;
      if (!isWinnerInMatch) {
        return;
      }
      const loserTeamId = winnerTeamId === homeTeamId
        ? awayTeamId
        : homeTeamId;
      const previousResult = state.prediction.matchResults[matchId];
      state.prediction.matchResults[matchId] = {
        winnerTeamId,
        loserTeamId,
      };
      if (
        previousResult &&
        (
          previousResult.winnerTeamId !== winnerTeamId ||
          previousResult.loserTeamId !== loserTeamId
        )
      ) {
        clearNextMatchResults(state, matchId);
      }
    },
    setFinalMatchScoreSide: (
      state,
      action: PayloadAction<SetFinalMatchScoreSidePayload>,
    ) => {
      if (state.prediction.hasUserPrediction) {
        return;
      }
      const { side, score } = action.payload;
      const finalMatch = Object
        .values(state.matches.byId)
        .find((match) => match.round === 'FINAL');
      if (!finalMatch) {
        return;
      }
      const homeTeamId = resolveWorldCupMatchSourceTeamId(finalMatch.homeSource, state);
      const awayTeamId = resolveWorldCupMatchSourceTeamId(finalMatch.awaySource, state);
      if (!homeTeamId || !awayTeamId) {
        return;
      }
      state.prediction.finalScorePrediction ??= {
        homeScore: null,
        awayScore: null,
      };
      if (side === 'home') {
        state.prediction.finalScorePrediction.homeScore = score;
      }
      if (side === 'away') {
        state.prediction.finalScorePrediction.awayScore = score;
      }
    },
    clearWorldCupPrediction: (state) => {
      if (state.prediction.hasUserPrediction) {
        return;
      }
      state.prediction.groupPlacements = {};
      state.prediction.bestThirdTeams = {
        BEST_THIRD_1: null,
        BEST_THIRD_2: null,
        BEST_THIRD_3: null,
        BEST_THIRD_4: null,
        BEST_THIRD_5: null,
        BEST_THIRD_6: null,
        BEST_THIRD_7: null,
        BEST_THIRD_8: null,
      };
      state.prediction.matchResults = {};
      state.prediction.finalScorePrediction = {
        homeScore: null,
        awayScore: null,
      };
    },
  },
  extraReducers: (builder) => {
    builder
      .addMatcher(
        worldCupForecastApi.endpoints.saveUserWorldCupPrediction.matchFulfilled,
        (state) => {
          state.prediction.hasUserPrediction = true;
        },
      )
      .addMatcher(
        worldCupForecastApi.endpoints.getUserWorldCupPrediction.matchFulfilled,
        (state, action) => {
          if (action.payload) {
            state.prediction = mapUserPredictionToWorldCupState(action.payload, state);
          }
        },
      );
  },
});

export const {
  setUserPrediction,
  selectGroupTeam,
  selectBestThirdTeam,
  selectMatchWinner,
  setFinalMatchScoreSide,
  clearWorldCupPrediction,
} = worldCupForecastSlice.actions;
